// treatment schema + Bedrock tool definition (TREATMENT_LOGGING_PLAN.md, D-TX-1..9).
// Pure data only — safe to import from both the API (Lambda) and the web SPA.
// Non-medication treatments/remedies (heat, ice, massage, breathing exercises, ...).
// Medications are explicitly out of scope: medication_taken/as_needed_med cover those,
// and prescribed PT exercises go through pt_session, not this type.

export const TREATMENT_CATEGORIES = [
  "heat",                   // heating pad, warm compress
  "cold",                   // ice pack, cold compress
  "contrast",               // alternating hot & cold
  "bath",                   // warm bath, epsom salt, oatmeal bath
  "shower_steam",           // hot shower, steam inhalation
  "massage",
  "stretching",
  "foam_rolling",
  "tens",                   // TENS / e-stim unit
  "compression",            // wrap, sleeve
  "brace_splint",
  "elevation",
  "rest",
  "nap_dark_room",
  "breathing_meditation",   // breathing exercises, meditation, relaxation
  "walk_fresh_air",
  "hydration_remedy",       // extra fluids, electrolytes, ginger/peppermint tea, honey
  "gargle",                 // salt-water gargle
  "humidifier",
  "topical",                // NON-DRUG topical: aloe, cooling gel (drug creams -> medication flow)
  "acupuncture_acupressure",
  "chiropractic",
  "distraction",            // music, game, company
  "other",
] as const;
export type TreatmentCategory = (typeof TREATMENT_CATEGORIES)[number];

export const TREATMENT_CATEGORY_LABELS: Record<TreatmentCategory, string> = {
  heat: "Heat", cold: "Cold / ice", contrast: "Hot & cold", bath: "Bath",
  shower_steam: "Shower / steam", massage: "Massage", stretching: "Stretching",
  foam_rolling: "Foam rolling", tens: "TENS unit", compression: "Compression",
  brace_splint: "Brace / splint", elevation: "Elevation", rest: "Rest",
  nap_dark_room: "Nap / dark room", breathing_meditation: "Breathing / meditation",
  walk_fresh_air: "Walk / fresh air", hydration_remedy: "Fluids / home remedy",
  gargle: "Gargle", humidifier: "Humidifier", topical: "Topical (non-drug)",
  acupuncture_acupressure: "Acupuncture / acupressure", chiropractic: "Chiropractic",
  distraction: "Distraction", other: "Other",
};

export const TREATMENT_EFFECTS = ["helped", "somewhat", "no_change", "worse"] as const;
export type TreatmentEffect = (typeof TREATMENT_EFFECTS)[number];

export const TREATMENT_EFFECT_LABELS: Record<TreatmentEffect, string> = {
  helped: "Helped", somewhat: "Helped a little", no_change: "No change", worse: "Felt worse",
};

/** Payload for a `treatment` event (TREATMENT_LOGGING_PLAN.md, D-TX-1..3). */
export interface TreatmentPayload {
  name: string;                    // R — free text, e.g. "ice pack on right knee"
  startedAt: string;               // R — ISO-8601 with offset (auto-now per D-10)
  category?: TreatmentCategory;    // O — fixed enum above
  durationMin?: number;            // O — minutes
  forSymptomName?: string;         // O — symptom this targets, as the user said it (D-TX-2)
  forSymptomKey?: string;          // A — symptomFollowUpKey(forSymptomName), written server-side
  effect?: TreatmentEffect;        // O — subjective outcome (D-TX-3)
  effectRatedAt?: string;          // A — ISO-8601, set server-side whenever effect is set
  aiSuggested?: boolean;           // A — true if AI proposed and user confirmed
  notes?: string;                  // O
}

/**
 * Bedrock tool: the model calls this when the user reports trying a non-medication
 * treatment or remedy. NOT for drugs/supplements (record_medication) and NOT for
 * prescribed physical-therapy exercises (record_pt_session).
 */
export const RECORD_TREATMENT_TOOL = {
  name: "record_treatment",
  description:
    "Record a non-medication treatment or remedy the user tried for a symptom or for general " +
    "relief — e.g. heat pad, ice pack, hot & cold, massage, warm bath, stretching, TENS unit, " +
    "compression, elevation, rest, nap, breathing exercises, salt-water gargle, steam, " +
    "humidifier, herbal tea, acupuncture, chiropractic. NOT for drugs or supplements " +
    "(use record_medication) and NOT for prescribed physical-therapy exercises " +
    "(use record_pt_session). If the user says what symptom it was for, pass forSymptomName. " +
    "If they say whether it worked, pass effect. Omit startedAt unless they said when.",
  input_schema: {
    type: "object",
    properties: {
      name: { type: "string", description: "The treatment as the user described it." },
      category: { type: "string", enum: [...TREATMENT_CATEGORIES] },
      startedAt: { type: "string", description: "ISO-8601 with UTC offset. Omit if not stated." },
      durationMin: { type: "number", description: "Duration in minutes, if stated." },
      forSymptomName: { type: "string", description: "Symptom this treatment targeted, if stated." },
      effect: {
        type: "string",
        enum: [...TREATMENT_EFFECTS],
        description: "Only if the user said whether it helped. Never guess.",
      },
      notes: { type: "string" },
    },
    required: ["name"],
  },
} as const;
